
import { useState } from 'react'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import { term } from '@/lib/ui-mode'

import Link from 'next/link'
import type { Edge } from '@xyflow/react'
import { SignalChip } from '@/components/library/SignalChip'
import { Icon } from '@/components/ui/Icon'
import { CATEGORY_META, SLOT_BUS_LABELS, slotDescriptor } from '@/lib/categories'
import { describeDevice, formatFormFactor, metaLine } from '@/lib/signals'
import { cableTypeFor, reachForRun, type CableProduct } from '@/lib/cables'
import { CABLE_GRADES, GRADE_INFO, HDMI_GRADES, HDMI_GRADE_INFO } from '@/lib/cable-grades'
import { SDI_RATE_LABELS, SHOW_FORMATS, formatByKey } from '@/lib/formats'
import { btn, cx, iconBtn, input, label as labelCls, notice } from '@/lib/ui'
import {
  partitionPorts,
  portIdFromHandle,
  splitPorts,
  unitLabel,
  type DeviceDTO,
  type InventoryItemDTO,
  type PortDTO,
} from '@/lib/types'
import type { DeviceFlowNode } from './types'
import { PatchForm } from './PatchForm'

type InspectorProps = {
  node: DeviceFlowNode | null
  /** The selected cable, when no node is selected. */
  edge: Edge | null
  edges: Edge[]
  nodes: DeviceFlowNode[]
  portById: Map<string, PortDTO>
  inventory: InventoryItemDTO[]
  /** inventoryItemId → nodeId for units already placed on this canvas. */
  unitsInUse: Map<string, string>
  onLabelChange: (nodeId: string, label: string) => void
  onUnitChange: (nodeId: string, inventoryItemId: string | null) => void
  onEdgeLabelChange: (edgeId: string, label: string) => void
  onEdgeLengthChange: (edgeId: string, lengthM: number | null) => void
  onEdgeFieldChange: (
    edgeId: string,
    key: 'cableGrade' | 'formatOverride' | 'cableId',
    value: string | null,
  ) => void
  /** The catalogue a run may pick a real product from. */
  cables: CableProduct[]
  /** The show's format; a run inherits it unless it carries an override. */
  showFormat: string | null
  /** Sets every cable touching one device at once. */
  onNodeCableLengths: (nodeId: string, lengthM: number | null) => void
  onRemove: (nodeId: string) => void
  onRemoveEdge: (edgeId: string) => void
  /** Patch a cable without a pointer; see PatchForm. */
  onPatch: (sourceNodeId: string, sourceHandle: string, targetNodeId: string, targetHandle: string) => void
  validateConnection: (
    sourceNodeId: string,
    sourceHandle: string,
    targetNodeId: string,
    targetHandle: string,
  ) => { ok: true } | { ok: false; reason: string | null }
  /** "/w/<slug>/library" — where a computer's slots are edited. */
  libraryPath: string
  /** Shrinks the panel to its rail. */
  onCollapse: () => void
}

const heading = 'text-[11px] font-medium uppercase tracking-[0.08em] text-muted'

/** "Slot 2 · DeckLink 8K Pro G2" for a port copied from an installed card, else null. */
function slotTextFor(device: DeviceDTO | undefined, port: PortDTO | undefined): string | null {
  if (!device || !port?.slotId) return null
  const slot = device.slots.find((candidate) => candidate.id === port.slotId)
  return slot ? slotDescriptor(slot.label, slot.card?.model) : null
}

export function Inspector(props: InspectorProps) {
  return (
    // Below xl this is a drawer over the canvas rather than a third column.
    // It used to simply not exist under 1280px — which is a 13-inch laptop at
    // default scaling, and every iPad — so there was no way to set a cable
    // length, rename a device or read the reach warning, while the right-click
    // menu went on offering those actions and silently doing nothing.
    <aside
      className={cx(
        'glass z-40 flex min-h-0 w-72 max-w-[calc(100vw-1.5rem)] shrink-0 flex-col rounded-[var(--radius-panel)]',
        'fixed inset-y-3 right-3 shadow-[var(--shadow-float)]',
        'xl:static xl:inset-auto xl:z-auto xl:max-w-none xl:shadow-none',
      )}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-[var(--border)] py-2.5 pl-4 pr-3">
        <h2 className={heading}>Details</h2>
        <button
          type="button"
          onClick={props.onCollapse}
          aria-label="Hide details"
          aria-expanded={true}
          title="Hide details (])"
          className={cx(iconBtn, 'h-7 w-7')}
        >
          <Icon name="chevronRight" className="h-4 w-4" />
        </button>
      </div>
      {props.node ? (
        <NodeInspector {...props} node={props.node} />
      ) : props.edge ? (
        <EdgeInspector {...props} edge={props.edge} />
      ) : (
        <Guide />
      )}
    </aside>
  )
}

function EdgeInspector({
  edge,
  nodes,
  portById,
  onEdgeLabelChange,
  onEdgeLengthChange,
  onEdgeFieldChange,
  showFormat,
  cables,
  onRemoveEdge,
}: InspectorProps & { edge: Edge }) {
  const { advanced } = useUiMode()
  const source = nodes.find((node) => node.id === edge.source)
  const target = nodes.find((node) => node.id === edge.target)
  const sourcePort = edge.sourceHandle ? portById.get(portIdFromHandle(edge.sourceHandle)) : undefined
  const targetPort = edge.targetHandle ? portById.get(portIdFromHandle(edge.targetHandle)) : undefined
  const name = (node: DeviceFlowNode | undefined) => node?.data.label || node?.data.device.name || '—'
  const sourceSlot = slotTextFor(source?.data.device, sourcePort)
  const targetSlot = slotTextFor(target?.data.device, targetPort)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        <div>
          <h2 className={heading}>Selected cable</h2>
          <div className="mt-2 rounded-xl bg-surface/70 p-3 shadow-sm">
            <p className="text-sm">
              <span className="font-medium">{name(source)}</span>
              <span className="text-muted"> · {sourcePort?.name ?? '—'}</span>
            </p>
            {sourceSlot && <p className="text-[11px] text-muted">{sourceSlot}</p>}
            <p className="my-1 flex items-center gap-1.5 text-xs text-muted">
              <Icon name="arrowRight" className="h-3.5 w-3.5 rotate-90" />
              {sourcePort?.connector ?? ''}
            </p>
            <p className="text-sm">
              <span className="font-medium">{name(target)}</span>
              <span className="text-muted"> · {targetPort?.name ?? '—'}</span>
            </p>
            {targetSlot && <p className="text-[11px] text-muted">{targetSlot}</p>}
            {sourcePort && (
              <p className="mt-2">
                <SignalChip signal={sourcePort.signal} />
              </p>
            )}
          </div>
        </div>

        <div>
          <label className={labelCls} htmlFor="edge-length">
            Run length
          </label>
          <div className="mt-1.5 flex items-center gap-2">
            <input
              id="edge-length"
              type="number"
              min={0.1}
              max={10000}
              step={0.5}
              className={cx(input, 'tabular-nums')}
              value={typeof edge.data?.lengthM === 'number' ? edge.data.lengthM : ''}
              onChange={(event) => {
                const value = event.target.value
                onEdgeLengthChange(edge.id, value === '' ? null : Number(value))
              }}
              placeholder="—"
            />
            <span className="text-sm text-muted">m</span>
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-muted">
            How far the cable actually runs, not how far apart the boxes look on
            screen. Feeds the pack list.
          </p>
        </div>

        <CableSpec
          edge={edge}
          sourcePort={sourcePort}
          targetPort={targetPort}
          showFormat={showFormat}
          cables={cables}
          advanced={advanced}
          onFieldChange={onEdgeFieldChange}
        />

        {/* Taping an ID on every cable is a rigging discipline, not something
            a two-cable setup needs a field for. */}
        {advanced && (
        <div>
          <label className={labelCls} htmlFor="edge-label">
            Cable ID
          </label>
          <input
            id="edge-label"
            className={cx(input, 'mt-1.5 font-mono')}
            value={typeof edge.label === 'string' ? edge.label : ''}
            onChange={(event) => onEdgeLabelChange(edge.id, event.target.value)}
            placeholder="V-014"
            maxLength={60}
          />
          <p className="mt-1.5 text-[11px] leading-relaxed text-muted">
            What is written on the tape. Left blank, the run sheet numbers it automatically.
          </p>
        </div>
        )}
      </div>

      <div className="shrink-0 border-t border-[var(--border)] p-3">
        <button type="button" onClick={() => onRemoveEdge(edge.id)} className={cx(btn.base, btn.danger, 'w-full')}>
          <Icon name="trash" className="h-4 w-4" />
          Remove cable
        </button>
      </div>
    </div>
  )
}

function Guide() {
  return (
    <div className="space-y-6 p-4">
      <div>
        <h2 className={heading}>Shortcuts</h2>
        <ul className="mt-2.5 space-y-2 text-xs leading-relaxed text-muted">
          <li>
            <Kbd>⌘</Kbd>/<Kbd>Ctrl</Kbd> + <Kbd>S</Kbd> save canvas
          </li>
          <li>
            <Kbd>Delete</Kbd> remove selected node or cable
          </li>
          <li>
            <Kbd>⌘</Kbd>/<Kbd>Ctrl</Kbd> + <Kbd>C</Kbd> / <Kbd>V</Kbd> copy and paste devices
          </li>
          <li>
            <Kbd>⌘</Kbd>/<Kbd>Ctrl</Kbd> + <Kbd>D</Kbd> duplicate the selection
          </li>
          <li>Right-click a device, a cable or the canvas for a menu</li>
          <li>
            <Kbd>[</Kbd> hide or show the library
          </li>
          <li>
            <Kbd>]</Kbd> hide or show this panel
          </li>
        </ul>
      </div>

      <div>
        <h2 className={heading}>How it works</h2>
        <ul className="mt-2.5 space-y-2 text-xs leading-relaxed text-muted">
          <li>Drag from an output handle to an input to lay a cable.</li>
          <li>Cables only join ports of the same signal category.</li>
          <li>Use the signal legend on the canvas to isolate one signal path.</li>
          <li>Long port runs collapse — click a run to open it.</li>
          <li>Select a device to rename the instance and see its patching.</li>
          <li>Select a cable to give it the ID written on the tape.</li>
        </ul>
      </div>
    </div>
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="mx-0.5 rounded-md border border-[var(--border)] bg-surface px-1.5 py-0.5 font-mono text-[10px] text-foreground shadow-sm">
      {children}
    </kbd>
  )
}

function NodeInspector({
  node,
  nodes,
  edges,
  portById,
  inventory,
  unitsInUse,
  onLabelChange,
  onUnitChange,
  onNodeCableLengths,
  onRemove,
  libraryPath,
  onPatch,
  validateConnection,
}: InspectorProps & { node: DeviceFlowNode }) {
  const { advanced } = useUiMode()
  const { device, label, inventoryItemId } = node.data
  const { inputs, outputs, bidirectional } = splitPorts(device.ports)
  const units = inventory.filter((item) => item.deviceId === device.id)
  const assignedUnit = inventoryItemId ? units.find((item) => item.id === inventoryItemId) : undefined
  const isComputer = device.category === 'COMPUTER' || device.slots.length > 0
  const specEntries: Array<[string, string | null]> = [
    ['CPU', device.cpu],
    ['RAM', device.ram],
    ['GPU', device.gpu],
    ['Storage', device.storage],
    ['OS', device.os],
  ]
  const specs = specEntries.filter((entry): entry is [string, string] => Boolean(entry[1]))
  const slotPortCounts = partitionPorts(device).bySlot

  const connected = edges.filter(
    (edge) => edge.source === node.id || edge.target === node.id,
  )

  const patchedPortIds = new Set(
    connected
      .flatMap((edge) => [
        edge.source === node.id ? edge.sourceHandle : null,
        edge.target === node.id ? edge.targetHandle : null,
      ])
      .filter((value): value is string => Boolean(value))
      .map(portIdFromHandle),
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        <div>
          <h2 className={heading}>Selected device</h2>
          <p className="mt-2 flex items-center justify-between gap-3 text-base font-semibold tracking-tight">
            <span className="min-w-0 truncate">{label || device.name}</span>
            <Icon
              name={CATEGORY_META[device.category].icon}
              strokeWidth={2}
              className="h-5 w-5 shrink-0 text-foreground/60"
              aria-label={CATEGORY_META[device.category].singular}
            />
          </p>
          <p className="text-xs text-muted">
            {describeDevice(device.manufacturer, device.model)}
          </p>
          {metaLine(formatFormFactor(device.formFactorRU), device.ipAddress) && (
            <p className="mt-1 font-mono text-[10px] text-muted">
              {metaLine(formatFormFactor(device.formFactorRU), device.ipAddress)}
            </p>
          )}
        </div>

        {isComputer && (
          <div className="space-y-4">
            {specs.length > 0 && (
              <div>
                <h3 className={heading}>Specs</h3>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                  {specs.map(([name, value]) => (
                    <div key={name} className="contents">
                      <dt className="text-muted">{name}</dt>
                      <dd className="truncate text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            <div>
              <h3 className={heading}>Slots</h3>
              {device.slots.length === 0 ? (
                <p className="mt-2 text-[11px] text-muted">No slots.</p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {device.slots.map((slot) => (
                    <li key={slot.id} className="rounded-xl bg-surface/70 px-3 py-2 shadow-sm">
                      <p className="text-[11px] font-medium">
                        {slot.label}
                        <span className="font-normal text-muted"> · {SLOT_BUS_LABELS[slot.bus]}</span>
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-muted">
                        {slot.card
                          ? `${slot.card.model} · ${slotPortCounts.get(slot.id)?.length ?? 0} ports`
                          : 'Empty'}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              <Link
                href={`${libraryPath}?device=${device.id}&edit=1`}
                className="mt-2 inline-block text-[11px] text-accent underline underline-offset-2"
              >
                Edit in library
              </Link>
            </div>
          </div>
        )}

        <div>
          <label className={labelCls} htmlFor="node-label">
            {advanced ? 'Instance label' : 'Name on this canvas'}
          </label>
          <input
            id="node-label"
            className={cx(input, 'mt-1.5')}
            value={label ?? ''}
            onChange={(event) => onLabelChange(node.id, event.target.value)}
            placeholder={device.name}
          />
          <p className="mt-1.5 text-[11px] leading-relaxed text-muted">
            Names this unit on the canvas and on the {term(advanced, 'runSheet').toLowerCase()} —
            “Camera 3”, “Rack B PDU”.
          </p>
        </div>

        {/* Inventory is an Advanced-only section — it is not in the rail in
            simple mode — so its control does not belong here either. An
            assignment already made in advanced mode still shows, read only,
            because hiding it would leave an unexplained name on the run sheet:
            simple hides controls, never consequences. */}
        {!advanced && assignedUnit && (
          <div>
            <span className={labelCls}>Which box this is</span>
            <p className="mt-1.5 text-[11px] text-muted">{unitLabel(assignedUnit)}</p>
          </div>
        )}

        {advanced && (
        <div>
          <label className={labelCls} htmlFor="node-unit">
            Inventory unit
          </label>
          {units.length === 0 ? (
            <p className="mt-1.5 text-[11px] text-muted">
              No units of this device in the inventory yet.
            </p>
          ) : (
            <select
              id="node-unit"
              className={cx(input, 'mt-1.5')}
              value={inventoryItemId ?? ''}
              onChange={(event) => onUnitChange(node.id, event.target.value || null)}
            >
              <option value="">Not assigned</option>
              {units.map((item) => {
                const takenBy = unitsInUse.get(item.id)
                const elsewhere = takenBy !== undefined && takenBy !== node.id
                return (
                  <option key={item.id} value={item.id} disabled={elsewhere}>
                    {unitLabel(item)}
                    {item.status !== 'AVAILABLE' ? ` · ${item.status.toLowerCase()}` : ''}
                    {elsewhere ? ' · on this canvas' : ''}
                  </option>
                )
              })}
            </select>
          )}
          <p className="mt-1.5 text-[11px] leading-relaxed text-muted">
            Which physical box this is. Shows on the run sheet so the crew loads the right case.
          </p>
        </div>
        )}

        <PatchForm
          node={node}
          nodes={nodes}
          onPatch={(sourceHandle, targetNodeId, targetHandle) =>
            onPatch(node.id, sourceHandle, targetNodeId, targetHandle)
          }
          validate={validateConnection}
        />

        {connected.length > 0 && (
          <div>
            <h3 className={heading}>Cable runs</h3>
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted">
              A camera position is one distance, not {connected.length}. Set every
              cable on this device at once.
            </p>
            <BulkLength count={connected.length} onApply={(m) => onNodeCableLengths(node.id, m)} />
          </div>
        )}

        <div>
          <h3 className={heading}>
            Patching ({patchedPortIds.size} of {device.ports.length} ports)
          </h3>
          {connected.length === 0 ? (
            <p className="mt-2 text-xs text-muted/80">Nothing patched to this device yet.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {connected.map((edge) => {
                const isOutgoing = edge.source === node.id
                const localPortId = isOutgoing ? edge.sourceHandle : edge.targetHandle
                const remotePortId = isOutgoing ? edge.targetHandle : edge.sourceHandle
                const localPort = localPortId
                  ? portById.get(portIdFromHandle(localPortId))
                  : undefined
                const remotePort = remotePortId
                  ? portById.get(portIdFromHandle(remotePortId))
                  : undefined
                const remoteNodeId = isOutgoing ? edge.target : edge.source
                const remoteDevice = nodes.find((candidate) => candidate.id === remoteNodeId)?.data.device
                const localSlot = slotTextFor(device, localPort)
                const remoteSlot = slotTextFor(remoteDevice, remotePort)
                return (
                  <li key={edge.id} className="rounded-xl bg-surface/70 px-3 py-2 shadow-sm">
                    <p className="flex items-center gap-1.5 text-[11px]">
                      <span className="font-mono text-muted">{isOutgoing ? 'OUT' : 'IN'}</span>
                      <span className="truncate font-medium">{localPort?.name ?? '—'}</span>
                      {localPort && <SignalChip signal={localPort.signal} />}
                    </p>
                    {localSlot && <p className="truncate text-[11px] text-muted">{localSlot}</p>}
                    <p className="mt-0.5 truncate text-[11px] text-muted">
                      {isOutgoing ? '→ ' : '← '}
                      {remotePort?.name ?? 'unknown port'}
                      {remotePort ? ` (${remotePort.connector})` : ''}
                      {remoteSlot ? ` · ${remoteSlot}` : ''}
                    </p>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 font-mono text-[11px] text-muted">
          <p>{inputs.length} inputs</p>
          <p>{outputs.length} outputs</p>
          {bidirectional.length > 0 && (
            <p className="col-span-2">{bidirectional.length} bi-directional</p>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-[var(--border)] p-3">
        <button
          type="button"
          onClick={() => onRemove(node.id)}
          className={cx(btn.base, btn.danger, 'w-full')}
        >
          <Icon name="trash" className="h-4 w-4" />
          Remove from canvas
        </button>
      </div>
    </div>
  )
}

/**
 * Sets the length of every cable on one device.
 *
 * The reason lengths never get filled in is that doing it one edge at a time
 * is unbearable past about twenty cables, and a real show has two hundred.
 * Crews already think in positions — "camera three is forty metres out" — so
 * this matches how the number is actually known.
 */
function BulkLength({ count, onApply }: { count: number; onApply: (lengthM: number | null) => void }) {
  const [value, setValue] = useState('')
  const apply = () => {
    const metres = Number(value)
    if (!value.trim() || !Number.isFinite(metres) || metres <= 0) return
    onApply(metres)
    setValue('')
  }
  return (
    <div className="mt-2 flex items-center gap-2">
      <input
        type="number"
        min={0.1}
        max={10000}
        step={0.5}
        aria-label="Length for every cable on this device"
        className={cx(input, 'tabular-nums')}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            apply()
          }
        }}
        placeholder="metres"
      />
      <button type="button" onClick={apply} disabled={!value.trim()} className={cx(btn.sm, btn.secondary, 'shrink-0')}>
        Set all {count}
      </button>
    </div>
  )
}

/**
 * What kind of cable this run is, and what it is carrying.
 *
 * Both drive the same question — will this reach? — so they sit together, and
 * the answer is shown underneath rather than left for the pack list to reveal.
 * A grade picker with no consequence on screen is a field people ignore.
 */
function CableSpec({
  edge,
  sourcePort,
  targetPort,
  showFormat,
  cables,
  advanced,
  onFieldChange,
}: {
  edge: Edge
  sourcePort: PortDTO | undefined
  targetPort: PortDTO | undefined
  showFormat: string | null
  onFieldChange: (
    edgeId: string,
    key: 'cableGrade' | 'formatOverride' | 'cableId',
    value: string | null,
  ) => void
  cables: CableProduct[]
  advanced: boolean
}) {
  if (!sourcePort || !targetPort) return null
  const type = cableTypeFor(sourcePort.signal, sourcePort.connector, targetPort.connector)
  const isHdmi = type.family.key === 'hdmi'
  const isCoax = ['sdi-coax', 'aes'].includes(type.family.key)
  // Only coax and HDMI have a grade worth choosing. An XLR lead is an XLR lead.
  if (!isHdmi && !isCoax) return null

  const grade = typeof edge.data?.cableGrade === 'string' ? edge.data.cableGrade : null
  const override = typeof edge.data?.formatOverride === 'string' ? edge.data.formatOverride : null
  const cableId = typeof edge.data?.cableId === 'string' ? edge.data.cableId : null
  const effective = override ?? showFormat
  const length = typeof edge.data?.lengthM === 'number' ? edge.data.lengthM : null
  const product = cableId ? (cables.find((c) => c.id === cableId) ?? null) : null
  // Only cables that could actually terminate this run.
  const matching = cables.filter((c) => c.family === type.family.key)
  const reach = reachForRun(type, grade, effective, product)
  const over = reach.limitM != null && length != null && length > reach.limitM

  // Simple mode hides the pickers and keeps the answer. Someone who does not
  // know what 12G-SDI is still needs to be told the cable will not reach.
  if (!advanced) {
    if (!over || reach.limitM == null) return null
    return (
      <p className={cx(notice.warning, 'text-[11px] leading-relaxed')}>
        This run is <strong className="font-medium">{length} m</strong>, which is longer than a{' '}
        {isHdmi ? 'plain HDMI lead' : 'normal video cable'} reliably carries (about {reach.limitM} m).
        {isHdmi
          ? ' Use an active or optical HDMI lead, or an extender.'
          : ' Use a better-grade cable, or convert to fibre.'}
      </p>
    )
  }

  return (
    <div>
      {matching.length > 0 && (
        <>
          <label className={cx(labelCls, 'block')} htmlFor="edge-cable">
            Cable
          </label>
          <select
            id="edge-cable"
            className={cx(input, 'mb-3 mt-1.5')}
            value={cableId ?? ''}
            onChange={(event) => onFieldChange(edge.id, 'cableId', event.target.value || null)}
          >
            <option value="">Not specified — use a general grade</option>
            {matching.map((cable) => (
              <option key={cable.id} value={cable.id}>
                {cable.manufacturer} {cable.partNumber}
              </option>
            ))}
          </select>
        </>
      )}

      {/* Hidden once a real product is named: its published figures replace
          the class typical entirely, so offering a grade as well would imply
          a choice that no longer has any effect. */}
      {!product && (
        <>
      <label className={labelCls} htmlFor="edge-grade">
        Cable type
      </label>
      <select
        id="edge-grade"
        className={cx(input, 'mt-1.5')}
        value={grade ?? ''}
        onChange={(event) => onFieldChange(edge.id, 'cableGrade', event.target.value || null)}
      >
        <option value="">{isHdmi ? 'High Speed (assumed)' : 'Standard broadcast coax (assumed)'}</option>
        {isHdmi
          ? HDMI_GRADES.map((key) => (
              <option key={key} value={key}>
                {HDMI_GRADE_INFO[key].label}
              </option>
            ))
          : CABLE_GRADES.map((key) => (
              <option key={key} value={key}>
                {GRADE_INFO[key].label}
              </option>
            ))}
      </select>
        </>
      )}

      {isCoax && (
        <>
          <label className={cx(labelCls, 'mt-3 block')} htmlFor="edge-format">
            Carrying
          </label>
          <select
            id="edge-format"
            className={cx(input, 'mt-1.5')}
            value={override ?? ''}
            onChange={(event) => onFieldChange(edge.id, 'formatOverride', event.target.value || null)}
          >
            <option value="">
              {showFormat
                ? `${formatByKey(showFormat)?.label ?? showFormat} — the show format`
                : 'No show format set'}
            </option>
            {SHOW_FORMATS.map((format) => (
              <option key={format.key} value={format.key}>
                {format.label}
              </option>
            ))}
          </select>
        </>
      )}

      <p
        className={cx(
          'mt-1.5 text-[11px] leading-relaxed',
          over ? 'font-medium text-danger' : 'text-muted',
        )}
      >
        {reach.limitM == null
          ? reach.note
          : over
            ? `${length} m is past the ~${reach.limitM} m this cable usually manages${reach.rate ? ` at ${SDI_RATE_LABELS[reach.rate]}` : ''}.`
            : `Good to about ${reach.limitM} m${reach.rate ? ` at ${SDI_RATE_LABELS[reach.rate]}` : ''}.`}
      </p>
    </div>
  )
}
